//rotatedStream.ts — canvas pass that rotates the frames actually published to Lucy

import type { QuarterTurns } from '../config/videoPreference'

export interface RotatedStream {
	readonly stream: MediaStream
	readonly stop: () => void
}

//Only built when rotation is actually needed. At 0 turns the raw camera track is published
//untouched, which keeps a frame of latency and a full canvas readback out of the hot path.
export function rotateStream(source: MediaStream, turns: QuarterTurns, fps = 30): RotatedStream | null {
	if (turns === 0) return null

	const track = source.getVideoTracks()[0]
	if (!track) return null

	const settings = track.getSettings()
	const sourceWidth = settings.width ?? 1280
	const sourceHeight = settings.height ?? 720
	//Odd quarter turns swap the axes, so the canvas is portrait where the camera was landscape.
	const swapped = turns === 1 || turns === 3
	const width = swapped ? sourceHeight : sourceWidth
	const height = swapped ? sourceWidth : sourceHeight

	const video = document.createElement('video')
	video.srcObject = source
	video.muted = true
	video.playsInline = true

	const canvas = document.createElement('canvas')
	canvas.width = width
	canvas.height = height
	const context = canvas.getContext('2d', { alpha: false })
	if (!context) return null

	let frame = 0
	const draw = () => {
		frame = requestAnimationFrame(draw)
		if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) return
		context.save()
		context.translate(width / 2, height / 2)
		context.rotate((turns * Math.PI) / 2)
		//Drawn about the centre in the source's own orientation, so the swap above is the
		//only place the axes are reasoned about.
		context.drawImage(video, -sourceWidth / 2, -sourceHeight / 2, sourceWidth, sourceHeight)
		context.restore()
	}

	const started = video.play().catch(() => undefined)
	void started.then(() => {
		frame = requestAnimationFrame(draw)
	})

	const stream = canvas.captureStream(fps)
	return {
		stream,
		stop: () => {
			cancelAnimationFrame(frame)
			video.srcObject = null
			stream.getTracks().forEach((t) => t.stop())
		},
	}
}
